import {
  LocationResponseSchema,
  MeResponseSchema,
  type ApiPaths,
  type LocationResponse,
  type MeResponse,
  type OrganisationCreateRequest,
  type OrganisationResponse,
  OrganisationResponseSchema,
} from "@safeplate/contracts";
import createClient from "openapi-fetch";

import { ApiError, apiErrorFromResponse, networkError } from "./errors.js";

const configuredBaseUrl = import.meta.env.VITE_API_URL?.trim();
export const apiBaseUrl = (configuredBaseUrl || "/v1").replace(/\/$/, "");

let activeOrganisationId: string | null = null;

export function setActiveOrganisationHeader(orgId: string | null): void {
  activeOrganisationId = orgId;
}

export async function requestApi<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  if (activeOrganisationId && !headers.has("X-Org-Id")) {
    headers.set("X-Org-Id", activeOrganisationId);
  }

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      ...init,
      credentials: "include",
      headers,
    });
  } catch (error) {
    throw networkError(error);
  }

  const body: unknown =
    response.status === 204
      ? undefined
      : await response.json().catch(() => null);
  if (!response.ok) {
    throw apiErrorFromResponse(response, body);
  }
  return body as T;
}

export const apiClient = createClient<ApiPaths>({
  baseUrl: apiBaseUrl,
  credentials: "include",
});

apiClient.use({
  onRequest({ request }) {
    if (activeOrganisationId && !request.headers.has("X-Org-Id")) {
      request.headers.set("X-Org-Id", activeOrganisationId);
    }
    return request;
  },
});

export async function getCurrentUser(): Promise<MeResponse> {
  try {
    const { data, error, response } = await apiClient.GET("/me");
    if (!response.ok || data === undefined) {
      throw apiErrorFromResponse(response, error);
    }
    return MeResponseSchema.parse(data);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw networkError(error);
  }
}

export async function getLocations(orgId: string): Promise<LocationResponse[]> {
  try {
    const { data, error, response } = await apiClient.GET("/locations", {
      params: { header: { "X-Org-Id": orgId } },
    });
    if (!response.ok || data === undefined) {
      throw apiErrorFromResponse(response, error);
    }
    return LocationResponseSchema.array().parse(data);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw networkError(error);
  }
}

export async function createOrganisation(
  input: OrganisationCreateRequest,
  idempotencyKey: string,
): Promise<OrganisationResponse> {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}/orgs`, {
      body: JSON.stringify(input),
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "Idempotency-Key": idempotencyKey,
      },
      method: "POST",
    });
  } catch (error) {
    throw networkError(error);
  }

  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw apiErrorFromResponse(response, body);
  }
  return OrganisationResponseSchema.parse(body);
}
